"use client";

import { useEffect, useRef, useState } from "react";
import type { IdeaNodeData, TestRun } from "./types";
import { inputCls, labelCls, uid } from "./constants";
import { TileCodeBlock } from "@/components/TileContent";

export function LlmTestPanel({
  data,
  onChange,
}: {
  data: IdeaNodeData;
  onChange: (data: IdeaNodeData) => void;
}) {
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const controller = useRef<AbortController | null>(null);
  const latest = useRef({ data, onChange });
  useEffect(() => {
    latest.current = { data, onChange };
  }, [data, onChange]);
  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );
  const prompt = (data.actionPayload?.userTemplate || "{{input}}").replaceAll(
    "{{input}}",
    data.testInput || "",
  );

  async function run() {
    if (controller.current) return;
    const abort = new AbortController();
    controller.current = abort;
    setRunning(true);
    setOutput("");
    setError("");
    const start = Date.now();
    const snapshot: TestRun = {
      id: uid(),
      at: new Date().toISOString(),
      input: data.testInput || "",
      systemPrompt: data.actionPayload?.systemPrompt || "",
      prompt,
      output: "",
      status: "success",
      durationMs: 0,
      model: "gemini-2.5-flash-lite",
    };
    try {
      if (/\{\{[^{}]+\}\}/.test(prompt))
        throw new Error(
          "Im Live-Test wird nur {{input}} ersetzt. Bitte weitere Platzhalter durch konkrete Werte ersetzen.",
        );
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        throw new Error("Du bist offline. Live-Tests erfordern eine Internetverbindung zu Gemini.");
      }
      const response = await fetch("/api/idea-scratcher/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ systemPrompt: snapshot.systemPrompt, prompt }),
        signal: abort.signal,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          body?.error || "Der Test konnte nicht gestartet werden.",
        );
      }
      if (!response.body) throw new Error("Keine Antwort vom Server.");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let complete = false;
      try {
        while (true) {
          const { value, done } = await reader.read();
          buffer += decoder.decode(value, { stream: !done });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.trim()) continue;
            const event = JSON.parse(line);
            if (event.type === "error") throw new Error(event.error);
            if (event.type === "text") {
              snapshot.output += event.text;
              setOutput(snapshot.output);
            }
            if (event.type === "done") complete = true;
          }
          if (done) break;
        }
      } finally {
        await reader.cancel().catch(() => {});
        reader.releaseLock();
      }
      if (!complete)
        throw new Error(
          "Die Verbindung wurde vorzeitig beendet. Die Antwort ist unvollständig.",
        );
    } catch (cause) {
      snapshot.status = abort.signal.aborted ? "cancelled" : "error";
      snapshot.error = abort.signal.aborted
        ? "Test abgebrochen."
        : cause instanceof Error
          ? cause.message
          : "Test fehlgeschlagen.";
      setError(snapshot.error);
    } finally {
      snapshot.durationMs = Date.now() - start;
      const current = latest.current;
      current.onChange({
        ...current.data,
        runs: [snapshot, ...(current.data.runs || [])].slice(0, 5),
      });
      controller.current = null;
      setRunning(false);
    }
  }

  return (
    <section className="space-y-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-4">
      <div>
        <h3 className="font-semibold">
          Live-Test · Gemini
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          Dieser Schritt wird einzeln getestet. Prompt und Test-Input werden an Gemini gesendet.
        </p>
      </div>
      <label className="block">
        <span className={labelCls}>Test-Input</span>
        <textarea
          aria-label="Test-Input"
          className={inputCls}
          rows={3}
          value={data.testInput || ""}
          onChange={(e) => onChange({ ...data, testInput: e.target.value })}
          placeholder="Wert für {{input}}"
        />
      </label>
      <label className="block">
        <span className={labelCls}>Erwarteter Output</span>
        <textarea
          className={inputCls}
          rows={2}
          value={data.expectedOutput || ""}
          onChange={(e) =>
            onChange({ ...data, expectedOutput: e.target.value })
          }
          placeholder="Was soll herauskommen? Zum manuellen Vergleich."
        />
      </label>
      <details>
        <summary className="cursor-pointer text-xs text-gray-500">
          Aufgelösten User-Prompt ansehen
        </summary>
        <p className="mt-2 whitespace-pre-wrap break-words text-xs">
          {prompt || "Noch kein Input"}
        </p>
      </details>
      <div className="flex gap-2">
        <button
          disabled={running || !prompt.trim()}
          onClick={run}
          className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {running ? "Modell antwortet…" : "Schritt testen"}
        </button>
        {running && (
          <button
            onClick={() => controller.current?.abort()}
            className="rounded-xl border px-4 py-2 text-sm"
          >
            Abbrechen
          </button>
        )}
      </div>
      {(running || output) && (
        <div>
          <span className={labelCls}>Live-Output</span>
          <div
            aria-label="Live-Output"
            className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-white p-3 text-sm dark:bg-zinc-900"
          >
            <TileCodeBlock language="markdown" title="Modellantwort">
              {output || "Warte auf das Modell…"}
            </TileCodeBlock>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-500">
          {error}
        </p>
      )}
      {!!data.runs?.length && (
        <div className="space-y-2">
          <span className={labelCls}>Letzte 5 Tests · lokal gespeichert</span>
          {data.runs.map((run) => (
            <details
              key={run.id}
              className="rounded-xl border border-gray-200 p-3 dark:border-white/10"
            >
              <summary className="cursor-pointer text-xs">
                {run.status === "success"
                  ? "Erfolgreich"
                  : run.status === "cancelled"
                    ? "Abgebrochen"
                    : "Fehler"}{" "}
                · {new Date(run.at).toLocaleString("de-DE")} ·{" "}
                {(run.durationMs / 1000).toFixed(1)} s
              </summary>
              <div className="mt-3 space-y-2 text-xs">
                <p className="text-gray-500">
                  {run.model || "gemini-2.5-flash-lite"}
                </p>
                {[
                  ["Input", run.input],
                  ["System-Prompt", run.systemPrompt],
                  ["User-Prompt", run.prompt],
                  ["Output", run.output],
                ].map(([label, value]) => (
                  <div key={label}>
                    <strong>{label}</strong>
                    <div className="max-h-48 overflow-auto">
                      <TileCodeBlock language="markdown" title={label}>
                        {value || "—"}
                      </TileCodeBlock>
                    </div>
                  </div>
                ))}
                {run.error && <p className="text-red-500">{run.error}</p>}
              </div>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
